import classNames from 'classnames'
import { navigate } from 'gatsby'
import React, { useContext } from 'react'

import { APIEmployee } from '../../../backend'
import useProducts from '../../hooks/useProducts'
import { LocaleContext } from '../../locale/i18n'
import { Product } from '../../logic/catalog'
import { renderRichTextWithHTML } from '../../logic/contentful'
import { populateContentfulMemberWithEmployeeData } from '../../logic/employee'
import {
  IContentfulLandingSection,
  TeamMember,
  TeamMemberPageLink,
} from '../../logic/shared-types'
import { isExternal } from '../../logic/url'
import Button from '../Button'
import Expert from '../Expert'
import MeasurImage from '../MeasurImage'
import ProductGrid from '../ProductGrid'
import Section, { BackgroundColor } from '../section/Section'
import './LandingSection.scss'

const TextColumn = ({
  heading,
  bodyText,
  center,
  ctaButtonLink,
  ctaButtonText,
  CTA,
}: Pick<
  IContentfulLandingSection,
  'heading' | 'bodyText' | 'ctaButtonLink' | 'ctaButtonText'
> & {
  center?: boolean
  CTA?: boolean
}) => {
  const { t } = useContext(LocaleContext)

  return (
    <div
      className={classNames(
        'landingSection__column',
        'landingSection__column--text',
        { 'landingSection__column--center': center },
      )}
    >
      <h2
        className={classNames('landingSection__heading', {
          'landingSection__heading--center': center,
        })}
      >
        {heading}
      </h2>
      <div className="richText landingSection__text">
        {bodyText && renderRichTextWithHTML({ text: bodyText })}
      </div>
      {CTA && (
        <Button
          size="xl"
          className="mb-3 max-w-82"
          onClick={() => {
            if (ctaButtonLink) {
              if (isExternal(ctaButtonLink)) {
                window.open(ctaButtonLink, '_blank')
                return
              }
              void navigate(ctaButtonLink)
              return
            }
            void navigate(`${location.pathname}#askForOffer`)
          }}
        >
          {ctaButtonText || t('common.askForOffer')}
        </Button>
      )}
    </div>
  )
}

const GraphicsColumn = ({
  member: member,
  expertHeading,
  expertIntro,
  image,
}: Pick<
  IContentfulLandingSection,
  'expert' | 'expertHeading' | 'expertIntro' | 'image'
> & { member?: TeamMember }) => {
  if (member) {
    return (
      <div
        className={classNames(
          'landingSection__colum',
          'landingSection__column--expert',
        )}
      >
        <Expert intro={expertIntro} heading={expertHeading} member={member} />
      </div>
    )
  }
  if (image?.gatsbyImageData) {
    return (
      <div className="landingSection__column">
        <MeasurImage
          className="rounded"
          image={image.gatsbyImageData}
          alt={image.title || ''}
        />
      </div>
    )
  }
  return null
}

export const LandingSection = ({
  section,
  pageName,
  employees,
  teamMemberPageLinks,
  variant,
  reverse,
}: {
  pageName: string
  section: IContentfulLandingSection
  employees: APIEmployee[]
  teamMemberPageLinks: TeamMemberPageLink[]
  variant?: BackgroundColor
  reverse?: boolean
}) => {
  const { locale } = useContext(LocaleContext)
  const products = useProducts()

  const {
    ctaButtonLink,
    ctaButtonText,
    contentful_id,
    heading,
    bodyText,
    image,
    expert,
    productIds,
    showCtaButton,
  } = section

  const employee = expert
    ? populateContentfulMemberWithEmployeeData(
        expert,
        employees,
        teamMemberPageLinks,
        locale,
      )
    : undefined

  if (process.env.DEPLOYMENT_STAGE === 'production' && expert) {
    if (!employee) {
      throw new Error(`Invalid expert supplied in landing section ${heading}`)
    }
    if (!employee?.isActive) {
      throw new Error(
        `Inactive employee ${
          employee?.name.full ?? expert?.id
        } in landing section ${heading}`,
      )
    }
  }

  const selectedProducts = (productIds ?? [])
    .map(pid => products.find(p => p.pipedriveProductId === Number(pid)))
    .filter((p): p is Product => p !== undefined)

  return (
    <Section key={contentful_id} backgroundColor={variant}>
      <div
        className={classNames('landingSection', {
          'landingSection--center': !image?.gatsbyImageData && !expert,
          'landingSection--reverse':
            reverse && (image?.gatsbyImageData || expert),
        })}
      >
        <GraphicsColumn {...section} member={employee} />
        <TextColumn
          heading={heading}
          bodyText={bodyText}
          center={!image?.gatsbyImageData && !expert}
          ctaButtonLink={ctaButtonLink}
          ctaButtonText={ctaButtonText}
          CTA={showCtaButton && selectedProducts.length === 0}
        />
      </div>
      {selectedProducts.length > 0 ? (
        <ProductGrid pageName={pageName} products={selectedProducts} />
      ) : null}
    </Section>
  )
}
